import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, Table, Spinner, Alert, Button, Modal,
  Badge, Row, Col, Form, InputGroup, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { FiSearch, FiRefreshCcw, FiEye, FiCheckCircle, FiXCircle, FiClock } from "react-icons/fi";
import { MdOutlineRoute } from "react-icons/md";
import API from "../services/api";

/* ----------------------------- utils ----------------------------- */
const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) => (v === 0 ? "0" : (v == null || String(v).trim() === "" ? "—" : v));
const asYes = (v) => {
  if (typeof v === "boolean") return v;
  const s = String(v || "").toLowerCase();
  return s === "yes" || s === "true" || s === "1";
};
const phone = (x) => (x ? <a href={`tel:${x}`}>{x}</a> : "—");
const mail  = (x) => (x ? <a href={`mailto:${x}`}>{x}</a> : "—");

const MapA = ({lat,lng,text}) => {
  if (lat==null || lng==null) return <>{text || "—"}</>;
  return (
    <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer">
      {text || "View location"} <MdOutlineRoute className="ms-1" />
    </a>
  );
};

const StatusChip = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  const map = {
    approved: { bg:"chip-success", icon:<FiCheckCircle/> , text:"Approved" },
    rejected: { bg:"chip-danger",  icon:<FiXCircle/>    , text:"Rejected" },
    pending:  { bg:"chip-secondary", icon:<FiClock/>     , text:"Pending"  },
    "in-progress": { bg:"chip-info", icon:<FiClock/>, text:"In-Progress" },
    inprogress: { bg:"chip-info", icon:<FiClock/>, text:"In-Progress" },
  };
  const m = map[s] || map.pending;
  return <span className={`chip ${m.bg}`}><i className="chip-icon">{m.icon}</i>{m.text}</span>;
};

const AvailChip = ({ value }) => {
  return asYes(value)
    ? <span className="chip chip-success"><i className="chip-icon"><FiCheckCircle/></i>Yes</span>
    : <span className="chip chip-dark"><i className="chip-icon"><FiXCircle/></i>No</span>;
};

/* ========================= PAGE ========================= */
export default function EmployeeReportHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await API.get("/employee/reports/my-history");
        setRows(Array.isArray(data) ? data : []);
        setErr("");
      } catch (e) {
        console.error(e);
        setErr("Failed to load history");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && (r.status || "pending") !== status) return false;
      if (!s) return true;
      const blob = [
        r.vendor?.companyName, r.vendor?.fullName, r.vendor?.mobile,
        r.requirement?.productType, r.items, r.message,
        r.employee?.fullName, r.employee?.mobile, r.employee?.email
      ].filter(Boolean).join(" | ").toLowerCase();
      return blob.includes(s);
    });
  }, [rows, q, status]);

  return (
    <Container className="mt-3">
      <Card className="report-card shadow-soft">
        {/* Top bar */}
        <div className="rc-topbar">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0 fw-semibold">📑 Report History</h5>
            <span className="count-pill">{filtered.length}</span>
            <span className="text-muted small">of {rows.length} total</span>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <InputGroup className="rc-search">
              <InputGroup.Text><FiSearch/></InputGroup.Text>
              <Form.Control
                placeholder="title / vendor / note / product"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q ? (
                <Button variant="outline-secondary" onClick={() => setQ("")}>Clear</Button>
              ) : null}
            </InputGroup>

            <Form.Select
              className="rc-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </Form.Select>

            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              <FiRefreshCcw className="me-1" /> Refresh
            </Button>
          </div>
        </div>

        {err && <Alert className="m-3" variant="danger">{err}</Alert>}

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : (
          <div className="table-wrap">
            <Table hover className="rc-table align-middle">
              <thead>
                <tr>
                  <th style={{minWidth: 210}}>Created</th>
                  <th>Status</th>
                  <th>Availability</th>
                  <th style={{minWidth: 220}}>Employee</th>
                  <th style={{minWidth: 220}}>Vendor</th>
                  <th style={{minWidth: 260}}>Requirement</th>
                  <th style={{minWidth: 240}}>Message</th>
                  <th style={{minWidth: 170}}>Admin Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length ? (
                  filtered.map((r) => {
                    const reqText = [
                      r.requirement?.productType || "—",
                      r.requirement?.quantity ? `${r.requirement.quantity} ${r.requirement.unit || ""}` : ""
                    ].filter(Boolean).join("  •  ");

                    const loc = r.requirement?.location || {};
                    const lat = loc?.lat ?? loc?.latitude;
                    const lng = loc?.lng ?? loc?.longitude;

                    return (
                      <tr key={r._id}
                          className={`row-tint ${r.status === "approved" ? "green" : r.status === "rejected" ? "red" : ""}`}>
                        <td>
                          <div className="tiny-id text-muted">{r._id}</div>
                          <div className="fw-semibold">{fmtIST(r.createdAt)}</div>
                        </td>

                        <td><StatusChip status={r.status} /></td>
                        <td><AvailChip value={r.hasRequirement} /></td>

                        <td>
                          <div className="entity">
                            <div className="title">{orDash(r.employee?.fullName || r.employee?.id)}</div>
                            <div className="meta">
                              {phone(r.employee?.mobile)} <span className="dot">•</span> {mail(r.employee?.email)}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="entity">
                            <div className="title">{orDash(r.vendor?.companyName || r.vendor?.fullName)}</div>
                            <div className="meta">{phone(r.vendor?.mobile)}</div>
                          </div>
                        </td>

                        <td>
                          <div className="entity">
                            <div className="title">{reqText || "—"}</div>
                            <div className="meta">
                              <MapA lat={lat} lng={lng} text={orDash(r.requirement?.locationText)} />
                            </div>
                          </div>
                        </td>

                        <td className="truncate">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{r.message || "—"}</Tooltip>}
                          >
                            <span>{orDash(r.message)}</span>
                          </OverlayTrigger>
                        </td>

                        <td className="text-nowrap">
                          {r.reviewedAt ? (
                            <>
                              <div className="meta">Reviewed: {fmtIST(r.reviewedAt)}</div>
                              <div className="meta">by {r.reviewedByName || "Admin"}</div>
                            </>
                          ) : <span className="text-muted">Pending review</span>}
                          <div className="mt-2">
                            <Button size="sm" className="btn-view" onClick={() => setSel(r)}>
                              <FiEye className="me-1" /> View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={8} className="text-center text-muted py-4">No reports found.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <ReportDetails report={sel} onHide={() => setSel(null)} />
    </Container>
  );
}

/* ====================== Details Modal ====================== */
function ReportDetails({ report, onHide }) {
  const show = !!report;

  const meta = useMemo(() => {
    if (!report) return null;

    const loc = report.requirement?.location || {};
    const lat = loc?.lat ?? loc?.latitude;
    const lng = loc?.lng ?? loc?.longitude;

    return {
      when: report.createdAt,
      status: report.status || "pending",
      employee: report.employee || {},
      vendor: report.vendor || {},
      req: report.requirement || {},
      available: asYes(report.hasRequirement),
      budget: report.budget || "",
      process: report.process || "",
      items: report.items || "",
      message: report.message || "",
      admin: {
        at: report.reviewedAt,
        by: report.reviewedByName || "",
        note: report.adminNote || "",
      },
      extra: report.extra || {},
      attachments: Array.isArray(report.attachments) ? report.attachments : [],
      lat, lng
    };
  }, [report]);

  if (!show) return null;

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          📄 Report Details
          <StatusChip status={meta.status} />
          <span className="text-muted small">• {fmtIST(meta.when)}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {/* top quick facts */}
        <div className="mb-3 d-flex flex-wrap align-items-center gap-3">
          <div><strong>Availability:</strong> <AvailChip value={meta.available} /></div>
          {meta.budget ? <div><strong>Budget:</strong> {meta.budget}</div> : null}
          {meta.items ? <div><strong>Items:</strong> {meta.items}</div> : null}
        </div>

        <Row className="g-3">
          <Col md={6}>
            <Card className="rc-subcard h-100">
              <Card.Body>
                <Card.Title>📦 Requirement</Card.Title>
                <div className="mb-1"><strong>Product:</strong> {orDash(meta.req.productType)}</div>
                <div className="mb-1"><strong>Quantity:</strong>{" "}
                  {meta.req.quantity != null ? `${meta.req.quantity} ${meta.req.unit || ""}` : "—"}</div>
                <div className="mb-1"><strong>Location:</strong>{" "}
                  <MapA lat={meta.lat} lng={meta.lng} text={orDash(meta.req.locationText)} /></div>
                <div className="mb-1"><strong>Notes:</strong> {orDash(meta.req.notes)}</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="rc-subcard h-100">
              <Card.Body>
                <Card.Title>🏢 Vendor</Card.Title>
                <div className="mb-1"><strong>Company:</strong> {orDash(meta.vendor.companyName)}</div>
                <div className="mb-1"><strong>Contact:</strong> {orDash(meta.vendor.fullName)}</div>
                <div className="mb-1"><strong>Mobile:</strong> {phone(meta.vendor.mobile)}</div>
                <div className="mb-1"><strong>Email:</strong> {mail(meta.vendor.email)}</div>
                <div className="mb-1"><strong>GST:</strong> {orDash(meta.vendor.gstNumber)}</div>
                <div className="mb-1"><strong>Address:</strong> {orDash(meta.vendor.address)}</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="rc-subcard h-100">
              <Card.Body>
                <Card.Title>🙋 Employee</Card.Title>
                <div className="mb-1"><strong>Name:</strong> {orDash(meta.employee.fullName || meta.employee.id)}</div>
                <div className="mb-1"><strong>Mobile:</strong> {phone(meta.employee.mobile)}</div>
                <div className="mb-1"><strong>Email:</strong> {mail(meta.employee.email)}</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="rc-subcard h-100">
              <Card.Body>
                <Card.Title>📝 Report</Card.Title>
                <div className="mb-1"><strong>Process:</strong> {orDash(meta.process)}</div>
                <div className="mb-1"><strong>Message:</strong> {orDash(meta.message)}</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="rc-subcard h-100">
              <Card.Body>
                <Card.Title>📈 Availability Details</Card.Title>
                <Row className="gy-2">
                  <Info label="Available Qty" value={meta.extra?.availableQty} />
                  <Info label="Price / Unit" value={meta.extra?.pricePerUnit ? `${meta.extra.pricePerUnit} ${meta.extra.currency||""}` : "—"} />
                  <Info label="MOQ" value={meta.extra?.moq} />
                  <Info label="Lead Time (days)" value={meta.extra?.leadTimeDays} />
                  <Info label="Available From" value={meta.extra?.availableFrom} />
                  <Info label="Payment Terms" value={meta.extra?.paymentTerms} />
                  <Info label="Transport Mode" value={meta.extra?.transportMode} />
                  <Info label="Delivery Mode" value={meta.extra?.deliveryMode} />
                  <Info label="Delivery Location (override)" value={meta.extra?.deliveryLocationOverride} full />
                </Row>

                {meta.attachments?.length ? (
                  <>
                    <hr />
                    <div><strong>Attachments:</strong></div>
                    <ul className="mb-0">
                      {meta.attachments.map((p, i) => (
                        <li key={i}><a href={p} target="_blank" rel="noreferrer">File {i+1}</a></li>
                      ))}
                    </ul>
                  </>
                ) : null}

                {meta.admin.at && (
                  <>
                    <hr />
                    <div className="text-muted small">
                      Reviewed on {fmtIST(meta.admin.at)} by {meta.admin.by || "Admin"}{meta.admin.note ? ` — ${meta.admin.note}` : ""}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

function Info({label, value, full}) {
  return (
    <Col md={full ? 12 : 4}>
      <div className="rc-info">
        <div className="rc-info-label">{label}</div>
        <div className="rc-info-value">{orDash(value)}</div>
      </div>
    </Col>
  );
}
